import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIF, UI } from '../design/theme';
import PetAvatar from '../design/components/PetAvatar';
import PillButton from '../design/components/PillButton';
import SunsetGradient from '../design/components/SunsetGradient';
import PawIcon from '../design/components/PawIcon';
import { buildTheme } from '../design/theme';
import { setStoredPet } from './storage';
import { PERSONA_LIST, INTERESTS, setStoredPersona, setStoredInterests, DEFAULT_PERSONA } from './persona';
import { V2_API, apiPost, isLoggedIn } from './api';
import '../design/animations.css';

const SUGGESTIONS = ['มะลิ', 'ขนุน', 'มอจิ', 'ลาเต้', 'โอลีฟ'];
const SPECIES = [
  { id: 'dog',    label: 'หมา',     emoji: '🐕' },
  { id: 'cat',    label: 'แมว',     emoji: '🐈' },
  { id: 'rabbit', label: 'กระต่าย', emoji: '🐇' },
  { id: 'other',  label: 'อื่นๆ',   emoji: '🦜' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const theme = buildTheme(localStorage.getItem('tl_v2_theme') || 'coral');
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState(null);
  const [interests, setInterests] = useState([]);
  const [name, setName] = useState('');
  const [species, setSpecies] = useState('dog');

  // Owner branch: Welcome → Persona → Name → Species → Ready
  // Other branch: Welcome → Persona → Interests → Ready
  const isOwner = persona === 'owner';
  const totalSteps = isOwner ? 5 : 4;

  const finish = async () => {
    if (persona) setStoredPersona(persona);
    setStoredInterests(interests);
    if (isOwner) setStoredPet({ name: name || 'มะลิ', species });

    // Best-effort sync to backend
    if (isLoggedIn()) {
      try {
        await apiPost(`${V2_API}/profile/persona`, {
          persona: persona || DEFAULT_PERSONA,
          interests,
        });
      } catch {}
    }
    navigate('/v2');
  };

  const toggleInterest = (id) => {
    setInterests(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div
      data-testid="v2-onboarding"
      className="tl-v2-fade-in"
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${theme.sunsetA}22 0%, ${theme.bg} 60%)`,
        color: theme.text, fontFamily: UI,
        maxWidth: 540, margin: '0 auto',
        display: 'flex', flexDirection: 'column',
        paddingTop: 'max(70px, calc(env(safe-area-inset-top) + 30px))',
      }}
    >
      <div style={{ display: 'flex', gap: 6, padding: '0 24px', marginBottom: 32 }}>
        {[...Array(totalSteps)].map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= step ? theme.primary : theme.skeleton,
            transition: 'background 300ms',
          }}/>
        ))}
      </div>

      {/* Step 0 — Welcome */}
      {step === 0 && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column' }}>
          <SunsetGradient theme={theme} radius={28} style={{ aspectRatio: '1', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'relative' }}>
              <PetAvatar size={140} name="มะลิ" species="cat" theme={theme}/>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  position: 'absolute', top: -30 + i*20, left: -50 - i*15, opacity: 0.7 - i*0.2,
                  transform: `rotate(${-20 + i*10}deg)`, color: '#fff',
                }}>
                  <PawIcon size={22 - i*4}/>
                </div>
              ))}
            </div>
          </SunsetGradient>
          <h1 style={{ fontFamily: SERIF, fontSize: 42, fontWeight: 600, margin: 0, lineHeight: 1.05, letterSpacing: -1 }}>
            สวัสดี!<br/>เราคือ <span style={{ color: theme.primary, fontStyle: 'italic' }}>TailLink</span>
          </h1>
          <p style={{ fontSize: 16, color: theme.muted, lineHeight: 1.55, marginTop: 14 }}>
            แอปของน้อง — เพื่อสุนัข แมว สัตว์ป่า และคนที่รักพวกเขาทุกตัว 🐾
          </p>
          <div style={{ flex: 1 }}/>
          <PillButton theme={theme} size="lg" full onClick={() => setStep(1)} testId="v2-onboard-start">
            เริ่มเลย →
          </PillButton>
          <button
            data-testid="v2-onboard-skip"
            onClick={() => navigate('/v2')}
            style={{ marginTop: 14, marginBottom: 40, background: 'transparent', border: 'none', color: theme.muted, fontSize: 13, cursor: 'pointer' }}
          >
            มีบัญชีแล้ว · เข้าสู่ระบบ
          </button>
        </div>
      )}

      {/* Step 1 — Persona */}
      {step === 1 && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, lineHeight: 1.15, letterSpacing: -0.6 }}>
            คุณกับสัตว์<br/>คือ <span style={{ color: theme.primary, fontStyle: 'italic' }}>?</span>
          </h1>
          <p style={{ fontSize: 14, color: theme.muted, marginTop: 10 }}>
            เราจะปรับแอปให้เข้ากับคุณ
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
            {PERSONA_LIST.map(p => {
              const active = persona === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`v2-onboard-persona-${p.id}`}
                  onClick={() => setPersona(p.id)}
                  style={{
                    padding: 16, borderRadius: 22, textAlign: 'left',
                    border: `2px solid ${active ? theme.primary : theme.cardBorder}`,
                    background: active ? `${theme.primary}10` : theme.surface,
                    cursor: 'pointer', transition: 'all 200ms',
                    display: 'flex', alignItems: 'center', gap: 14,
                  }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: active ? theme.primary : theme.skeleton,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, color: '#fff',
                  }}>{p.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: theme.text }}>{p.label}</div>
                    <div style={{ fontFamily: UI, fontSize: 12, color: theme.muted, marginTop: 2 }}>{p.desc}</div>
                  </div>
                  {active && (
                    <div style={{ color: theme.primary, fontSize: 22 }}>✓</div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1 }}/>
          <PillButton
            theme={theme}
            size="lg" full
            disabled={!persona}
            onClick={() => setStep(2)}
            testId="v2-onboard-persona-next"
          >
            ต่อไป →
          </PillButton>
          <div style={{ height: 40 }}/>
        </div>
      )}

      {/* Step 2 — Owner: Name | Other: Interests */}
      {step === 2 && isOwner && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 36, fontWeight: 600, margin: 0, lineHeight: 1.1, letterSpacing: -0.8 }}>
            น้องชื่อ<br/>อะไรนะ? <span style={{ color: theme.primary }}>🐾</span>
          </h1>
          <p style={{ fontSize: 15, color: theme.muted, marginTop: 10 }}>เราจะเรียกน้องด้วยชื่อนี้ทั้งแอป</p>
          <div style={{ marginTop: 32 }}>
            <input
              data-testid="v2-onboard-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="เช่น มะลิ, ขนุน, มอจิ..."
              style={{
                width: '100%', boxSizing: 'border-box', padding: '20px 22px',
                borderRadius: 20, border: `2px solid ${theme.cardBorder}`,
                background: theme.surface, color: theme.text,
                fontFamily: SERIF, fontSize: 28, fontWeight: 500, outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {SUGGESTIONS.map(n => (
              <button key={n} data-testid={`v2-onboard-suggest-${n}`} onClick={() => setName(n)} style={{
                padding: '8px 14px', borderRadius: 999,
                border: `1px solid ${theme.cardBorder}`,
                background: name === n ? theme.primary : theme.surface,
                color: name === n ? '#fff' : theme.text,
                fontFamily: UI, fontSize: 13, cursor: 'pointer',
              }}>{n}</button>
            ))}
          </div>
          <div style={{ flex: 1 }}/>
          <PillButton theme={theme} size="lg" full onClick={() => setStep(3)} testId="v2-onboard-next-name">
            ต่อไป →
          </PillButton>
          <div style={{ height: 40 }}/>
        </div>
      )}

      {step === 2 && !isOwner && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 32, fontWeight: 600, margin: 0, lineHeight: 1.1, letterSpacing: -0.6 }}>
            สนใจ<br/><span style={{ color: theme.primary, fontStyle: 'italic' }}>สัตว์แบบไหน</span>?
          </h1>
          <p style={{ fontSize: 14, color: theme.muted, marginTop: 10 }}>เลือกได้หลายอย่าง · เปลี่ยนทีหลังได้</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 24 }}>
            {INTERESTS.map(i => {
              const active = interests.includes(i.id);
              return (
                <button
                  key={i.id}
                  data-testid={`v2-onboard-interest-${i.id}`}
                  onClick={() => toggleInterest(i.id)}
                  style={{
                    padding: 16, borderRadius: 18,
                    border: `2px solid ${active ? theme.primary : theme.cardBorder}`,
                    background: active ? `${theme.primary}10` : theme.surface,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <div style={{ fontSize: 28 }}>{i.emoji}</div>
                  <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 600, color: theme.text }}>{i.label}</div>
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1 }}/>
          <PillButton
            theme={theme} size="lg" full
            disabled={interests.length === 0}
            onClick={() => setStep(3)}
            testId="v2-onboard-interests-next"
          >
            ต่อไป →
          </PillButton>
          <div style={{ height: 40 }}/>
        </div>
      )}

      {/* Step 3 — Owner: Species | Other: Ready */}
      {step === 3 && isOwner && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column' }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 36, fontWeight: 600, margin: 0, lineHeight: 1.1, letterSpacing: -0.8 }}>
            น้อง <span style={{ fontStyle: 'italic', color: theme.primary }}>{name || 'มะลิ'}</span><br/>เป็นใครเอ่ย?
          </h1>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 32 }}>
            {SPECIES.map(s => (
              <button
                key={s.id}
                data-testid={`v2-onboard-species-${s.id}`}
                onClick={() => setSpecies(s.id)}
                style={{
                  aspectRatio: '1', borderRadius: 24,
                  background: species === s.id ? `linear-gradient(135deg, ${theme.sunsetA}, ${theme.sunsetB})` : theme.surface,
                  border: `2px solid ${species === s.id ? 'transparent' : theme.cardBorder}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  fontFamily: SERIF, fontSize: 22, fontWeight: 600,
                  color: species === s.id ? '#fff' : theme.text,
                  cursor: 'pointer', transition: 'all 200ms',
                }}
              >
                <div style={{ fontSize: 44 }}>{s.emoji}</div>
                <div style={{ marginTop: 8 }}>{s.label}</div>
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }}/>
          <PillButton theme={theme} size="lg" full onClick={() => setStep(4)} testId="v2-onboard-next-species">
            ต่อไป →
          </PillButton>
          <div style={{ height: 40 }}/>
        </div>
      )}

      {((step === 4 && isOwner) || (step === 3 && !isOwner)) && (
        <div style={{ flex: 1, padding: '0 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ position: 'relative', marginBottom: 28 }}>
            <PetAvatar
              size={140}
              name={isOwner ? (name || 'มะลิ') : 'TL'}
              species={isOwner ? species : 'cat'}
              theme={theme}
              halo
            />
            {[...Array(12)].map((_, i) => {
              const angle = (i / 12) * Math.PI * 2;
              const r = 110;
              const colors = [theme.sunsetA, theme.sunsetB, theme.sunsetC, theme.safe];
              return (
                <div key={i} style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 10, height: 10, borderRadius: '50%',
                  background: colors[i % colors.length],
                  transform: `translate(${Math.cos(angle)*r}px, ${Math.sin(angle)*r}px)`,
                  animation: `tlPop 1.2s ease-out ${i*0.05}s both`,
                }}/>
              );
            })}
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: 38, fontWeight: 600, margin: 0, lineHeight: 1.1, letterSpacing: -0.8 }}>
            ยินดีต้อนรับ<br/>
            <span style={{ color: theme.primary, fontStyle: 'italic' }}>
              {isOwner ? (name || 'มะลิ') : 'เพื่อนรักสัตว์'}!
            </span>
          </h1>
          <p style={{ fontSize: 15, color: theme.muted, marginTop: 14, maxWidth: 280 }}>
            {isOwner
              ? 'น้องๆ ใกล้ๆ รอเจออยู่ ไปดูกันมั้ย?'
              : 'มีน้องเยอะมากที่รอคุณช่วย ไปดูกันเลย'}
          </p>
          <div style={{ flex: 1 }}/>
          <div style={{ width: '100%' }}>
            <PillButton theme={theme} size="lg" full onClick={finish} testId="v2-onboard-finish">
              {isOwner ? 'ไปบ้านของน้อง 🏠' : 'ไปดูเพื่อนๆ 🐾'}
            </PillButton>
          </div>
          <div style={{ height: 40 }}/>
        </div>
      )}
    </div>
  );
}
